import {
  Badge,
  Button,
  IcnCheck,
  IcnRefresh,
  IcnZap,
} from '@viquard/doc-ui';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { BrowserPushState } from '../push-registration.js';

export function NotificationPushPanel(props: {
  pushState: BrowserPushState;
  pushStateLoading: boolean;
  registerPending: boolean;
  unregisterPending: boolean;
  onEnable: () => void;
  onDisable: () => void;
  onRefresh: () => void;
}): ReactNode {
  const { t } = useTranslation('settings');
  const {
    pushState,
    pushStateLoading,
    registerPending,
    unregisterPending,
    onEnable,
    onDisable,
    onRefresh,
  } = props;

  return (
    <section
      className="flex flex-col gap-4"
      style={{
        padding: 'var(--vq-space-5)',
        background: 'var(--vq-paper-raised)',
        border: '1px solid var(--vq-rule)',
        borderRadius: 'var(--vq-radius-lg)',
      }}
      aria-label={t('notifications.push.title')}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="m-0 flex items-center gap-2 text-base font-semibold">
            <IcnZap size={16} /> {t('notifications.push.title')}
          </h3>
          <p className="m-0 mt-1 text-sm text-[color:var(--vq-text-muted)]">
            {pushState.supported
              ? t('notifications.push.subtitle')
              : t(`notifications.push.reasons.${pushState.reason ?? 'server'}`)}
          </p>
        </div>
        <Badge
          tone={pushState.endpoint ? 'success' : pushState.supported ? 'info' : 'warning'}
          variant="soft"
          size="sm"
        >
          {pushState.endpoint
            ? t('notifications.push.subscribed')
            : pushState.supported
              ? t(`notifications.push.permission.${pushState.permission}`)
              : t('notifications.push.unavailable')}
        </Badge>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          variant="primary"
          iconLeft={<IcnCheck size={14} />}
          loading={registerPending || pushStateLoading}
          disabled={!pushState.supported || pushState.endpoint !== null}
          onClick={onEnable}
        >
          {t('notifications.push.enable')}
        </Button>
        <Button
          size="sm"
          variant="secondary"
          loading={unregisterPending}
          disabled={pushState.endpoint === null}
          onClick={onDisable}
        >
          {t('notifications.push.disable')}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          iconLeft={<IcnRefresh size={14} />}
          loading={pushStateLoading}
          onClick={onRefresh}
        >
          {t('notifications.refresh')}
        </Button>
      </div>
    </section>
  );
}
